// =============================================================================
// Pintravel — Follows
// =============================================================================

import type { FollowListItem, FollowRelation, FollowSummary } from '@itravel/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { profileKeys } from '@/features/profile/api/profile.api';
import { socialKeys } from '@/features/social/api/social.api';
import { api } from '@/lib/api/instance';

import { inspirationKeys } from './inspirations.api';

interface Data<T> {
  data: T;
}

export type FollowDirection = 'followers' | 'following';

export const followKeys = {
  all: ['follows'] as const,
  requests: () => [...followKeys.all, 'requests'] as const,
  list: (userId: string, direction: FollowDirection) =>
    [...followKeys.all, 'list', userId, direction] as const,
  /**
   * Not under `all` on purpose: following re-reads everything there, and the
   * server leaves followed people out of suggestions — they would vanish under
   * the finger. useFollow re-marks them instead.
   */
  suggestions: (near: { lat: number; lng: number } | null) => ['follow-suggestions', near] as const,
};

/** People to follow for someone new: near them first, then from anywhere. */
export function useFollowSuggestions(near: { lat: number; lng: number } | null, enabled = true) {
  return useQuery({
    queryKey: followKeys.suggestions(near),
    queryFn: async () =>
      (
        await api
          .get('follows/suggestions', {
            searchParams: near ? { lat: String(near.lat), lng: String(near.lng) } : {},
          })
          .json<Data<FollowListItem[]>>()
      ).data,
    enabled,
    staleTime: Infinity,
  });
}

interface FollowListPage {
  data: FollowListItem[];
  meta: { page: number; limit: number; total: number; hasMore: boolean };
}

/** Who follows somebody, or whom they follow — each with my own relation to them. */
export function useFollowList(userId: string | undefined, direction: FollowDirection) {
  return useInfiniteQuery({
    queryKey: followKeys.list(userId ?? '', direction),
    queryFn: async ({ pageParam }) =>
      api
        .get(`follows/${userId}/${direction}`, {
          searchParams: { page: String(pageParam), limit: '30' },
        })
        .json<FollowListPage>(),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.hasMore ? last.meta.page + 1 : undefined),
    enabled: Boolean(userId),
  });
}

export function useFollowRequests(enabled = true) {
  return useQuery({
    queryKey: followKeys.requests(),
    queryFn: async () => (await api.get('follows/requests').json<Data<FollowSummary[]>>()).data,
    enabled,
    staleTime: 30_000,
  });
}

/** Follow / unfollow. The profile re-reads itself so the button and the counts agree. */
export function useFollow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, follow }: { userId: string; follow: boolean }) =>
      (
        await (follow ? api.post(`follows/${userId}`) : api.delete(`follows/${userId}`)).json<
          Data<{ relation: FollowRelation }>
        >()
      ).data,
    // Everywhere a person's follow state shows: profiles, feeds, lists, search.
    onSuccess: ({ relation }, { userId }) => {
      queryClient.setQueriesData<FollowListItem[]>({ queryKey: ['follow-suggestions'] }, (list) =>
        list?.map((person) => (person.id === userId ? { ...person, relation } : person)),
      );
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
      void queryClient.invalidateQueries({ queryKey: inspirationKeys.all });
      void queryClient.invalidateQueries({ queryKey: followKeys.all });
      void queryClient.invalidateQueries({ queryKey: socialKeys.all });
    },
  });
}

export function useAnswerFollowRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, accept }: { userId: string; accept: boolean }) => {
      await api.post(`follows/requests/${userId}/${accept ? 'accept' : 'decline'}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: followKeys.all });
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}
